import { useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Anchor, Badge, Button, Center, Group, Loader, Pagination, Paper, Pill, Select, Stack, Table, Text,
  TextInput, Tooltip,
} from '@mantine/core'
import {
  IconAlertCircle, IconFilterOff, IconInfoCircle, IconPlugConnectedX, IconRefresh, IconSearch,
} from '@tabler/icons-react'
import { EmptyState, HairlineDivider, PageTitle } from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { ReportAccessDrawer } from '../components/access/ReportAccessDrawer'
import { CartographerLink } from '../components/cartographer/CartographerLink'
import { RegistryStatus } from '../components/registry/ReportRegistryPreview'
import {
  ALL_STATUSES, GROUP_MEMBERS_NOT_RESOLVABLE, NO_REGISTRY_FILTERS, REGISTRY_SURFACE, SEARCH_SCOPE_CAPTION,
  asStatusFilter, grantsText, groupOrRoleGrantsText, isRegistryFiltered, lastRunWhen, modifiedWhen, ownerCell,
  ownerCellText, registryCountText, registryEmptyText, registryErrorText, registryFetchEnabled, registryWhen,
  runnableNote, schedulesText, statusFilterOptions, toRegistryQuery, workspaceCell,
} from '../components/registry/registryFormat'
import type { RegistryFilters, RegistryPick, RegistryWhen } from '../components/registry/registryFormat'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { useRegistry } from '../hooks/useRegistry'
import { isApiError } from '../services/apiError'
import { errorKind, exactNumber } from '../utils/format'
import { reportDetailPath } from '../utils/reportDetailPath'
import type { RegistryOverview, RegistryReport } from '../types/registry'

const TITLE_SUBTITLE = 'Chart Room — every report that exists, where it is filed, and who is accountable for it'

/**
 * Report Registry (the Chart Room) — every Cartographer report as an operational
 * asset, in every lifecycle status, across every workspace INCLUDING personal
 * ones.
 *
 * `GET api/observatory/registry`, LighthouseAdmin at class level. That the page
 * enumerates everybody's personal reports is a disclosure doc 17 §4 accepted for
 * Lighthouse.Admin holders only, which is why the gate is honoured here even
 * though the server enforces it too.
 *
 * Decisions that shape it:
 *
 *  1. **Read-only (A1, ruled as D6a).** No verb acts on a report. Transfers,
 *     archiving, restoring and sharing are Cartographer's, through its own gate.
 *  2. **An inventory, not a data surface (A2).** Nothing derived from a stored
 *     definition is served or rendered.
 *  3. **Every filter is the server's**, and paging is real. Nothing is counted,
 *     filtered or derived from "the rows on this page" — in particular there is
 *     no workspace dropdown, because no workspace-list endpoint exists and a list
 *     built from one page would present a window as the whole.
 *  4. **The owner is the served principal type**, not a guess. A group-owned
 *     report says a group owns it and that this makes nobody the owner.
 *  5. **Visibility is not a column** while the server says it is unenforced:
 *     every row reads Private by default, and printing that would report an
 *     access fact nobody measured.
 *  6. **No poll** (useRegistry): an explicit Refresh instead.
 *  7. **A row opens Report Detail** (`/registry/:reportId`, D5) — the row and the
 *     name both — and the row's **Access** button opens the access explanation in
 *     place (ReportAccessDrawer), the same answer Report Detail's Access tab gives.
 */
export function ReportRegistryPage() {
  const gate = useLighthouseAdmin()
  const navigate = useNavigate()

  // The box holds a draft; the query runs on the applied term, set on Enter or
  // the button, so typing does not fire a request (and two COUNTs) per keystroke.
  const [draftSearch, setDraftSearch] = useState('')
  const [filters, setFilters] = useState<RegistryFilters>(NO_REGISTRY_FILTERS)
  const [page, setPage] = useState(1)
  const [openReportId, setOpenReportId] = useState<string | null>(null)

  const query = toRegistryQuery(filters, page)
  const { data, isLoading, isError, error, isFetching, refetch } = useRegistry(query, {
    enabled: registryFetchEnabled(gate, 'page'),
  })

  const filtered = isRegistryFiltered(filters)

  const apply = (next: RegistryFilters) => {
    setFilters(next)
    setPage(1)
  }

  const clearAll = () => {
    setDraftSearch('')
    apply(NO_REGISTRY_FILTERS)
  }

  // Denied renders WITHOUT fetching: the query above is disabled on `denied`.
  if (gate.state === 'denied') {
    return (
      <Stack gap="lg" pb="xl">
        <PageTitle subtitle={TITLE_SUBTITLE}>Report Registry</PageTitle>
        <RestrictedState surface={REGISTRY_SURFACE} reason={gate.reason} />
      </Stack>
    )
  }

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle={TITLE_SUBTITLE}>Report Registry</PageTitle>

      <Text size="sm" c="dimmed">
        Read-only: nothing on this page changes a report. Transfers, archiving and sharing are made in
        Cartographer, through its own permissions — <CartographerLink path="/governance" label="Governance" />.
      </Text>

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <Stack gap="sm">
          <Group align="flex-end" gap="sm">
            <TextInput
              label="Search"
              description={SEARCH_SCOPE_CAPTION}
              placeholder="Report name or description"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply({ ...filters, search: draftSearch })
              }}
              w={280}
            />
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={() => apply({ ...filters, search: draftSearch })}
            >
              Search
            </Button>
            <Select
              label="Status"
              data={statusFilterOptions()}
              value={filters.status ?? ALL_STATUSES}
              onChange={(value) => apply({ ...filters, status: asStatusFilter(value) })}
              allowDeselect={false}
              w={190}
            />
            {filtered && (
              <Button variant="subtle" leftSection={<IconFilterOff size={16} />} onClick={clearAll}>
                Clear filters
              </Button>
            )}
            {/*
              An explicit refresh, because this query deliberately does not poll: a
              report changes when a person changes it in Cartographer, not on a clock.
            */}
            <Tooltip label="Ask the registry again">
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => void refetch()}
                loading={isFetching}
                ml="auto"
              >
                Refresh
              </Button>
            </Tooltip>
          </Group>

          {(filters.owner || filters.workspace || filters.search.trim()) && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">Filtered to:</Text>
              {filters.search.trim() && (
                <Pill withRemoveButton onRemove={() => { setDraftSearch(''); apply({ ...filters, search: '' }) }}>
                  Search “{filters.search.trim()}”
                </Pill>
              )}
              {filters.owner && (
                <Tooltip label={`Exact principal id ${filters.owner.id} — matched on id, never on name`}>
                  <Pill withRemoveButton onRemove={() => apply({ ...filters, owner: null })}>
                    Owner: {filters.owner.label}
                  </Pill>
                </Tooltip>
              )}
              {filters.workspace && (
                <Tooltip label={`Workspace ${filters.workspace.id}`}>
                  <Pill withRemoveButton onRemove={() => apply({ ...filters, workspace: null })}>
                    Workspace: {filters.workspace.label}
                  </Pill>
                </Tooltip>
              )}
            </Group>
          )}
        </Stack>
      </Paper>

      {/*
        `|| !data` is load-bearing: between React Query retries the flags read
        "loaded, no error" with no data, which must not render as an empty
        registry.
      */}
      {isError ? (
        <RegistryError error={error} filtered={filtered} onClear={clearAll} />
      ) : isLoading || !data ? (
        <Center py="xl"><Loader /></Center>
      ) : (
        <RegistryResults
          data={data}
          filtered={filtered}
          page={page}
          onPage={setPage}
          onOpen={(path) => navigate(path)}
          onAccess={setOpenReportId}
          onOwner={(owner) => apply({ ...filters, owner })}
          onWorkspace={(workspace) => apply({ ...filters, workspace })}
        />
      )}

      <ReportAccessDrawer reportId={openReportId} onClose={() => setOpenReportId(null)} />
    </Stack>
  )
}

function RegistryResults({
  data, filtered, page, onPage, onOpen, onAccess, onOwner, onWorkspace,
}: {
  data: RegistryOverview
  filtered: boolean
  page: number
  onPage: (page: number) => void
  /** Go to a report's Report Detail, by the path its row built. */
  onOpen: (path: string) => void
  /** The access explanation, in a drawer over this page. */
  onAccess: (reportId: string) => void
  onOwner: (owner: RegistryPick) => void
  onWorkspace: (workspace: RegistryPick) => void
}) {
  const rows = data.reports
  const empty = registryEmptyText(data, filtered)

  return (
    <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      <Stack gap="md">
        <Text fw={600}>{registryCountText(data, filtered)}</Text>

        {rows.length === 0 ? (
          <EmptyState
            icon={filtered ? <IconSearch size={28} stroke={1.5} /> : <IconInfoCircle size={28} stroke={1.5} />}
            title={empty.title}
            message={empty.message}
            action={data.totalCount > 0 && page > 1
              ? <Button variant="light" size="xs" onClick={() => onPage(1)}>Go to page 1</Button>
              : undefined}
          />
        ) : (
          <>
            {/* Ten columns, three of them timestamps: the table scrolls, the page never does. */}
            <Table.ScrollContainer minWidth={1250}>
              <Table striped highlightOnHover verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Report</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Workspace</Table.Th>
                    <Table.Th>Owner</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th>Modified</Table.Th>
                    <Table.Th>Last recorded run</Table.Th>
                    <Table.Th ta="right">Active schedules</Table.Th>
                    <Table.Th ta="right">People granted</Table.Th>
                    <Table.Th ta="right">
                      <Stack gap={0} align="flex-end">
                        <span>Group or role grants</span>
                        <Text span size="xs" c="dimmed" fw={400}>{GROUP_MEMBERS_NOT_RESOLVABLE}</Text>
                      </Stack>
                    </Table.Th>
                    {data.visibilityIsEnforced && <Table.Th>Visibility</Table.Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((report) => (
                    <RegistryRow
                      key={report.reportId}
                      report={report}
                      showVisibility={data.visibilityIsEnforced}
                      onOpen={onOpen}
                      onAccess={onAccess}
                      onOwner={onOwner}
                      onWorkspace={onWorkspace}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Page {data.pageNumber} of {data.totalPages}
                {' · '}
                {exactNumber(data.totalCount)} {filtered ? 'matching' : 'in the registry'}
              </Text>
              {data.totalPages > 1 && <Pagination value={page} total={data.totalPages} onChange={onPage} />}
            </Group>
          </>
        )}

        <HairlineDivider />
        <RegistryCaptions visibilityIsEnforced={data.visibilityIsEnforced} />
      </Stack>
    </Paper>
  )
}

function RegistryRow({
  report, showVisibility, onOpen, onAccess, onOwner, onWorkspace,
}: {
  report: RegistryReport
  showVisibility: boolean
  onOpen: (path: string) => void
  onAccess: (reportId: string) => void
  onOwner: (owner: RegistryPick) => void
  onWorkspace: (workspace: RegistryPick) => void
}) {
  const workspace = workspaceCell(report)
  const owner = ownerCell(report)
  const notRunnable = runnableNote(report)
  // The link target: what a click navigates to, and carried as data so a rendered row says where it goes.
  const detail = reportDetailPath(report.reportId)

  return (
    <Table.Tr style={{ cursor: 'pointer' }} data-report-detail={detail} onClick={() => onOpen(detail)}>
      <Table.Td style={{ maxWidth: 320 }}>
        {/*
          The name is the keyboard affordance for Report Detail — a <tr> takes no
          focus. stopPropagation on both buttons so a click here does not also
          fire the row's.
        */}
        <Anchor
          component="button"
          type="button"
          size="sm"
          fw={500}
          ta="left"
          data-report-detail={detail}
          onClick={(event) => {
            event.stopPropagation()
            onOpen(detail)
          }}
          aria-label={`Open ${report.name}`}
        >
          {report.name}
        </Anchor>
        {report.description && <Text size="xs" c="dimmed" lineClamp={2}>{report.description}</Text>}
        {/* In the Report cell, not a column of its own: it explains this report, it is not a figure. */}
        <Anchor
          component="button"
          type="button"
          size="xs"
          display="block"
          data-action="access"
          onClick={(event) => {
            event.stopPropagation()
            onAccess(report.reportId)
          }}
          aria-label={`Who can reach ${report.name}, and why`}
        >
          Access
        </Anchor>
      </Table.Td>
      <Table.Td>
        <RegistryStatus status={report.status} />
        {/* The server's runnable verdict: on a report that cannot run, the counts to the right are view-only or inert. */}
        {notRunnable && <Text size="xs" c="dimmed">{notRunnable}</Text>}
      </Table.Td>
      <Table.Td>
        {workspace.pick ? (
          <Group gap={6} wrap="nowrap">
            <Tooltip label={`${workspace.tooltip} — click to list only this workspace`}>
              <Anchor
                component="button"
                type="button"
                size="sm"
                ta="left"
                onClick={(event) => {
                  event.stopPropagation()
                  onWorkspace(workspace.pick!)
                }}
              >
                {workspace.text}
              </Anchor>
            </Tooltip>
            {workspace.personalWorkspace && <Badge size="xs" variant="outline" color="gray">personal workspace</Badge>}
          </Group>
        ) : (
          <Text size="sm">{workspace.text}</Text>
        )}
      </Table.Td>
      <Table.Td style={{ maxWidth: 280 }}>
        {owner.pick ? (
          <Tooltip label={`${owner.tooltip ?? owner.pick.id} — click to list only this owner's reports`} multiline w={320}>
            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="left"
              aria-label={ownerCellText(report)}
              onClick={(event) => {
                event.stopPropagation()
                onOwner(owner.pick!)
              }}
              style={{ overflowWrap: 'anywhere' }}
            >
              {owner.text}
            </Anchor>
          </Tooltip>
        ) : (
          <Text size="sm">{owner.text}</Text>
        )}
        {owner.note && <Text size="xs" c="dimmed">{owner.note}</Text>}
      </Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}><When when={registryWhen(report.createdAt)} /></Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}><When when={modifiedWhen(report.modifiedAt)} /></Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}><When when={lastRunWhen(report.lastRunAt)} /></Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{schedulesText(report.activeScheduleCount)}</Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {grantsText(report.userGrantCount)}
      </Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        <Text span size="sm" c={report.groupOrRoleGrantCount === 0 ? 'dimmed' : undefined}>
          {groupOrRoleGrantsText(report.groupOrRoleGrantCount)}
        </Text>
      </Table.Td>
      {showVisibility && <Table.Td>{report.visibility}</Table.Td>}
    </Table.Tr>
  )
}

/** The relative form, with the wall clock on hover when there is one. */
function When({ when }: { when: RegistryWhen }) {
  if (!when.full) return <Text size="sm">{when.text}</Text>
  return (
    <Tooltip label={when.full}>
      <Text size="sm" span>{when.text}</Text>
    </Tooltip>
  )
}

/**
 * What the columns cannot say about themselves. Shown in every loaded state,
 * the empty one included, because each is a way the table reads as more
 * definite than it is.
 */
function RegistryCaptions({ visibilityIsEnforced }: { visibilityIsEnforced: boolean }) {
  const lines: ReactNode[] = [
    <><strong>Owner</strong> is the business owner, falling back to the author. A name is shown only while
      the owner is still the report&rsquo;s creator: after a transfer no name for the new owner is recorded
      anywhere, so the cell shows a dash with the id on hover rather than the previous owner&rsquo;s name.</>,
    <><strong>Personal (not filed)</strong> is how Cartographer stores a personal report — filed in no
      workspace. This inventory includes every person&rsquo;s personal reports, which is why it is served only
      to holders of Lighthouse.Admin.</>,
    <><strong>Active schedules</strong> count every schedule marked active, including schedules on reports
      that can no longer run, such as archived ones. It is a count of schedule rows, not of deliveries; a row
      marked &ldquo;not runnable&rdquo; delivers nothing until its report is restored.</>,
    <><strong>People granted</strong> counts live grants to individual users. &ldquo;none explicit&rdquo; does
      not mean nobody can reach the report — owners, maintainers and workspace members reach it without a
      grant. On a report marked &ldquo;not runnable&rdquo; these grants are view-only: an archived report can
      still be opened read-only by its grantees, and run, export and schedule return on restore. A row&rsquo;s
      Access says who can, and why; the row itself opens the report&rsquo;s detail.</>,
    <><strong>Group or role grants</strong> are counted apart and never added to the people: {GROUP_MEMBERS_NOT_RESOLVABLE}
      {' '}— they are stored, not expanded, so they confer access on nobody, and no Cartographer path writes
      them today.</>,
    <><strong>Last recorded run</strong> is the last run Cartographer recorded. Until every open of a saved report
      was recorded, a saved report with no parameters opened in Spyglass recorded nothing, so &ldquo;Never&rdquo;
      means never recorded.</>,
  ]
  if (!visibilityIsEnforced) {
    lines.push(
      <><strong>Visibility is not shown.</strong> The column is enforced nowhere, so every row reads Private
        by default — a default nobody chose, not an access decision.</>,
    )
  }

  return (
    <Stack gap={4}>
      {lines.map((line, index) => <Text key={index} size="xs" c="dimmed">{line}</Text>)}
    </Stack>
  )
}

/** Restricted, refused, unreachable and broken each look different, and none reads as an empty registry. */
function RegistryError({ error, filtered, onClear }: { error: unknown; filtered: boolean; onClear: () => void }) {
  const kind = errorKind(error)

  if (kind === 'forbidden') {
    return <RestrictedState surface={REGISTRY_SURFACE} reason={isApiError(error) ? error.detail : null} />
  }

  if (kind === 'bad-request') {
    return (
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} />}
        title="The registry refused this filter"
        message={registryErrorText(error)}
        action={filtered ? <Button variant="light" size="xs" onClick={onClear}>Clear filters</Button> : undefined}
      />
    )
  }

  if (kind === 'unauthorized') {
    return (
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} />}
        title="Not signed in"
        message="The API did not accept this session's credentials. Sign in again, or check the API key."
      />
    )
  }

  if (kind === 'network') {
    return (
      <EmptyState
        icon={<IconPlugConnectedX size={28} stroke={1.5} />}
        title="Could not reach the API"
        message="The request never got an answer. This says nothing about which reports exist."
      />
    )
  }

  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title="Could not load the report registry"
      message={`${registryErrorText(error)} This says nothing about which reports exist.`}
    />
  )
}
